import {
  FLAT_NAVIGATION_SCHEMA,
  type FlatNavGroup,
  type FlatNavItem,
  getNavItemById,
  LEGAL_DIVISION_LABELS,
} from '@/config/navigation-schema';

/**
 * The scoped sidebars of the packages that do not get the full tree: the
 * compliance trial, the regulation package and the legal division. Each is
 * built from the canonical registry, so hrefs and gates stay identical to
 * the full sidebar; `getAccessibleNavGroups` picks which one a user gets.
 */

/**
 * Trial sidebar: Cari Peraturan as the extraction entry and the
 * compliance directory. Built from the canonical
 * registry so hrefs and gates stay identical to the full sidebar.
 */
export function getTrialNavGroups(): FlatNavGroup[] {
  const byId = new Map<string, FlatNavItem>();
  for (const group of FLAT_NAVIGATION_SCHEMA) {
    for (const item of group.items) {
      if (!byId.has(item.id)) byId.set(item.id, item);
    }
  }
  const groups: FlatNavGroup[] = [];
  const main: FlatNavItem[] = [];
  const search = byId.get('reg-search');
  if (search) main.push(search);
  if (main.length > 0) groups.push({ label: '', eyebrow: 'Menu Utama', items: main });
  const checklists = byId.get('workspace-checklists');
  if (checklists) {
    const regulationChecklist = byId.get('workspace-regulation-checklist');
    groups.push({
      label: 'Direktori Kepatuhan',
      eyebrow: 'Kepatuhan',
      collapsible: true,
      icon: 'workspace-checklists',
      items: [
        { ...checklists, badge: undefined },
        ...(regulationChecklist ? [{ ...regulationChecklist, badge: undefined }] : []),
      ],
    });
  }
  return groups;
}

/**
 * Regulation-package sidebar: the dashboard (which is where consolidations
 * are read and browsed) and search up front, then the user's own research
 * trail. Items come from the canonical registry so
 * hrefs and gates stay identical to the full sidebar.
 */
export function getRegulationProfessionalNavGroups(): FlatNavGroup[] {
  const pick = (ids: string[]): FlatNavItem[] =>
    ids.flatMap((id) => {
      const item = getNavItemById(id);
      return item ? [{ ...item, badge: undefined, hidden: false }] : [];
    });
  return [
    {
      label: '',
      eyebrow: 'Menu Utama',
      items: pick(['reg-dashboard', 'reg-search', 'reg-browse']),
    },
    { label: '', eyebrow: 'Riset Saya', items: pick(['reg-library', 'reg-history']) },
  ].filter((group) => group.items.length > 0);
}

/**
 * Legal-division sidebar: the workspace dashboard on top, then three groups
 * named for what the division does there, most frequent first: keep watch
 * over deadlines, cases, contracts, permits and assets; work with the AI;
 * look up the law. Every label comes from {@link LEGAL_DIVISION_LABELS}, in
 * Indonesian and in sentence case, and no group heading repeats the one item
 * under it. Items come from the canonical registry so hrefs and gates stay
 * identical; alpha flags are dropped because the package ships these
 * features. Live counts and the register list are added by the sidebar
 * (see `legal-division-nav`), not here.
 *
 * The court-decision directory that needs a browser extension is left out:
 * "Cari Putusan" searches decisions in the app itself.
 */
export function getLegalDivisionNavGroups(): FlatNavGroup[] {
  const pick = (entries: Array<[id: string, label: string]>): FlatNavItem[] =>
    entries.flatMap(([id, label]) => {
      const item = getNavItemById(id);
      return item ? [{ ...item, label, badge: undefined, isAlpha: false, hidden: false }] : [];
    });
  const labels = LEGAL_DIVISION_LABELS;
  const group = (eyebrow: string, items: FlatNavItem[]): FlatNavGroup => ({ label: '', eyebrow, plainEyebrow: true, items });
  return [
    // The overview sits above the groups: it summarizes all of them.
    { label: '', items: pick([['workspace-dashboard', labels.dashboard]]) },
    group(
      'Pantau',
      pick([
        ['legal-register-early-warning', labels.earlyWarning],
        ['cases-files', labels.cases],
        ['legal-register', labels.registers],
      ]),
    ),
    group(
      'Kerja dengan AI',
      pick([
        ['workspace-assistant', labels.assistant],
        ['document-review', labels.documentReview],
        ['tabular-reviews', labels.tabularReview],
        ['workspace-templates', labels.templates],
      ]),
    ),
    group(
      'Riset hukum',
      pick([
        ['reg-search', labels.regulationSearch],
        ['cc-putusan', labels.decisionSearch],
      ]),
    ),
  ].filter((g) => g.items.length > 0);
}
